import React, { useContext, useEffect, useState } from "react";
import { Button, Grid, TextField } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SmsIcon from "@mui/icons-material/Sms";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Commonmodel from "../Commonmodel/Commonmodel"; // your reusable modal
import "./AbhaCreateModal.css";
import { useNavigate } from "react-router-dom";
import { apisPath, paths } from "../../utils/path";
import { CommonContext } from "../../context/CommonContext";
import { authHttp } from "../../services/commonHttpService";
import { encryptTextApi, formatAbhaId, useResendOTP } from "../../helpers/commonHelper";
import { AuthContext } from "../../context/AuthContext";
import Step1AadhaarInput from "./Step1AadhaarInput/Step1AadhaarInput";
import Step2OtpMobileVerification from "./Step2OtpMobileVerification/Step2OtpMobileVerification";
import Step3AbhaAddressSuggestions from "./Step3AbhaAddressSuggestions/Step3AbhaAddressSuggestions";
import Step4AbhaRegistered from "./Step4AbhaRegistered/Step4AbhaRegistered";


const AbhaCreateModal = ({ open, onClose, handleSearchModalClose }) => {
  const navigate = useNavigate();
  const { setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);
  const { setAbhaProfile, userName } = useContext(AuthContext);

  // Step Management
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  // Step 1 - Aadhaar
  const [aadhaar, setAadhaar] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);

  // Step 2 - OTP & Mobile
  const [otp, setOtp] = useState("");
  const [mobile, setMobile] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [showMobileOtpStep, setShowMobileOtpStep] = useState(false);
  const [mobileOtpTxnId, setMobileOtpTxnId] = useState(null);

  // Common States
  const [abdmAccessToken, setAbdmAccessToken] = useState(null);
  const [txnId, setTxnId] = useState(null);
  const [otpMessage, setOtpMessage] = useState("");
  const [authToken, setAuthToken] = useState(null);

  // Store encrypted values for resend (to avoid re-encrypting)
  const [encryptedAadhaar, setEncryptedAadhaar] = useState(null);
  const [encryptedMobileNumber, setEncryptedMobileNumber] = useState(null);

  // Step 3 - ABHA Address
  const [abhaSuggestions, setAbhaSuggestions] = useState([]);
  const [selectedAbhaAddress, setSelectedAbhaAddress] = useState("");

  // Step 4 - ABHA Profile Data
  const [abhaData, setAbhaData] = useState(null);
  const [isExistingAccount, setIsExistingAccount] = useState(false);

  // ==================== PURE API FUNCTIONS FOR RESEND ====================

  // Pure API call for Aadhaar OTP (for resend)
  const sendAadhaarOtpApi = async () => {
    const cleanAadhaar = aadhaar.replace(/-/g, "");

    if (!cleanAadhaar) {
      throw new Error("Please enter Aadhaar number");
    }

    // Check if this is the first call or a resend
    const isFirstCall = !encryptedAadhaar;

    // Use stored encrypted value for resend, or encrypt for first time
    let encrypted = encryptedAadhaar;
    if (!encrypted) {
      encrypted = await encryptTextApi(cleanAadhaar);
      setEncryptedAadhaar(encrypted); // Store for resend
    }

    const response = await authHttp.post(
      apisPath?.sendAadhaarOtp,
      { loginId: encrypted },
      {
        headers: {
          accessToken: abdmAccessToken,
        },
      }
    );

    if (response.data?.success) {
      const responseData = response.data?.data;

      // ✅ CRITICAL: ALWAYS update txnId (including resends)
      if (responseData?.txnId) {
        setTxnId(responseData?.txnId);
      }

      setOtpMessage(responseData?.message || "OTP sent successfully");
      return responseData;
    } else {
      throw new Error(response.data?.message || "Failed to send OTP");
    }
  };

  // Pure API call for Mobile OTP (for resend)
  const sendMobileOtpApi = async (mobileNumber = null, currentTxnId = null) => {
    const mobileToUse = mobileNumber || mobile;
    const txnIdToUse = currentTxnId || txnId;

    if (!mobileToUse) {
      throw new Error("Please enter mobile number");
    }

    // Check if this is the first call or a resend
    const isFirstCall = !encryptedMobileNumber;

    // Use stored encrypted value for resend, or encrypt for first time
    let encrypted = encryptedMobileNumber;
    if (!encrypted) {
      encrypted = await encryptTextApi(mobileToUse);
      setEncryptedMobileNumber(encrypted); // Store for resend
    }

    const response = await authHttp.post(
      apisPath?.sendMobileUpdateOtp,
      {
        loginId: encrypted,
        txnId: txnIdToUse,
      },
      {
        headers: {
          accessToken: abdmAccessToken,
        },
      }
    );

    if (response?.data) {
      // ONLY update mobileOtpTxnId on first call, not on resends
      if (isFirstCall && response?.data?.data?.txnId) {
        setMobileOtpTxnId(response?.data?.data?.txnId);
      }

      return response?.data?.data;
    } else {
      throw new Error("Failed to send mobile OTP");
    }
  };

  // ==================== RESEND OTP HOOKS ====================

  // Hook for Aadhaar OTP resend
  const {
    countdown: aadhaarCountdown,
    resendCount: aadhaarResendCount,
    canResend: canResendAadhaar,
    formattedTime: aadhaarFormattedTime,
    handleResend: handleResendAadhaar,
    resetTimer: resetAadhaarTimer,
    startTimer: startAadhaarTimer,
    maxResendCount: aadhaarMaxResendCount
  } = useResendOTP({
    maxResendCount: 3,
    countdownSeconds: 60,
    onResend: async () => {
      try {
        setIsLoading(true);
        const responseData = await sendAadhaarOtpApi();

        setSnackState({
          open: true,
          message: responseData?.message || 'OTP resent successfully',
          severity: 'success',
        });
      } catch (error) {
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    autoStart: false
  });

  // Hook for Mobile OTP resend
  const {
    countdown: mobileCountdown,
    resendCount: mobileResendCount,
    canResend: canResendMobile,
    formattedTime: mobileFormattedTime,
    handleResend: handleResendMobile,
    resetTimer: resetMobileTimer,
    startTimer: startMobileTimer,
    maxResendCount: mobileMaxResendCount
  } = useResendOTP({
    maxResendCount: 3,
    countdownSeconds: 60,
    onResend: async () => {
      try {
        setIsLoading(true);
        const responseData = await sendMobileOtpApi(mobile, txnId);

        setSnackState({
          open: true,
          message: "OTP resent to mobile number",
          severity: 'success',
        });
      } catch (error) {
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    autoStart: false
  });

  // Fetch ABDM Access Token when modal opens, cleanup when modal closes
  useEffect(() => {
    if (open) {
      fetchAbdmAccessToken();
    }

    // Cleanup: Clear token when modal closes
    return () => {
      if (!open) {
        setAbdmAccessToken(null);
      }
    };
  }, [open]);

  // Fetch ABDM Access Token from API
  const fetchAbdmAccessToken = async () => {
    try {
      setIsLoading(true);

      const response = await authHttp.get(apisPath?.abdmAccessToken);

      if (response.data?.success && response.data?.accessToken) {
        setAbdmAccessToken(response?.data?.accessToken);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch ABDM access token');
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || 'Failed to fetch ABDM access token',
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Send Aadhaar OTP
  const handleSendAadhaarOtp = async () => {
    try {
      setIsLoading(true);

      // Use the pure API function
      const responseData = await sendAadhaarOtpApi();

      setSnackState({
        open: true,
        message: responseData?.message || "OTP sent successfully",
        severity: "success",
      });

      setStep(2);
      // Start Aadhaar timer
      resetAadhaarTimer();
      startAadhaarTimer();

    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to send OTP",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify Aadhaar OTP and Mobile
  const handleVerifyAadhaarOtp = async () => {
    try {
      setIsLoading(true);

      const encryptedOtp = await encryptTextApi(otp);

      const response = await authHttp.post(
        apisPath?.verifyAadhaarOtp,
        {
          txnId: txnId,
          otpValue: encryptedOtp,
          mobile: mobile,
        },
        {
          headers: {
            accessToken: abdmAccessToken,
          },
        }
      );

      if (response?.data?.success) {
        const profileData = response?.data?.data?.ABHAProfile;
        const tokens = response?.data?.data?.tokens;
        const isNew = response?.data?.data?.isNew;

        // Store the token for later use
        setAuthToken(tokens?.token);
        setTxnId(response?.data?.data?.txnId);

        // Check if account already exists (isNew = false)
        if (isNew === false) {
          // EXISTING ACCOUNT - Skip ABHA address creation
          setIsExistingAccount(true);

          // Compare mobile with trimmed strings
          const userMobile = mobile?.toString().trim();
          const profileMobile = profileData?.mobile?.toString().trim();

          if (profileMobile && profileMobile === userMobile) {
            // Scenario 2.1.1: Mobile matches + existing account
            const maskedAbhaNumber = formatAbhaId(profileData?.ABHANumber);

            setAlertState({
              open: true,
              title: `ABHA Account Exist - We have found an ABHA ${maskedAbhaNumber} against the Aadhaar Number provided by you.`,
              btnTxt: "OK",
              status: "success",
              onClose: () => {
                // After alert closes, verify and show profile
                // Pass token directly from response to avoid state timing issues
                handleVerifyAbhaUser(tokens?.token);
              }
            });
          } else {
            // Scenario 2.2.1: Mobile doesn't match + existing account
            // Need mobile verification first, then show existing account alert
            setShowMobileOtpStep(true);
            await handleSendMobileOtp(mobile, response?.data?.data?.txnId);
          }
        } else {
          // NEW ACCOUNT (isNew = true) - Need to create ABHA address
          setIsExistingAccount(false);

          if (profileData?.mobile) {
            // Scenario 2.1.2: Mobile matches + new account
            const userMobile = mobile?.toString().trim();
            const profileMobile = profileData?.mobile?.toString().trim();

            if (profileMobile === userMobile) {
              setSnackState({
                open: true,
                message: "Mobile number for communication is same as Aadhaar number. No additional information required.",
                severity: "success",
              });

              // Move to Step 3 for ABHA address creation
              setStep(3);
            } else {
              // Mobile values don't match, need verification
              setShowMobileOtpStep(true);
              await handleSendMobileOtp(mobile, response?.data?.data?.txnId);
            }
          } else {
            // Scenario 2.2.2: Mobile is null + new account
            setShowMobileOtpStep(true);
            await handleSendMobileOtp(mobile, response?.data?.data?.txnId);
          }
        }
      } else {
        throw new Error(response.data?.message || "Failed to verify OTP");
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to verify OTP",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2.2: Send Mobile Update OTP
  const handleSendMobileOtp = async (mobileNumber, currentTxnId) => {
    try {
      setIsLoading(true);

      // Use the pure API function
      const responseData = await sendMobileOtpApi(mobileNumber, currentTxnId);
      setSnackState({
        open: true,
        message: "OTP sent to mobile number",
        severity: "success",
      });

      // Start mobile timer
      resetMobileTimer();
      startMobileTimer();
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to send mobile OTP",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2.2: Verify Mobile OTP
  const handleVerifyMobileOtp = async () => {
    try {
      setIsLoading(true);

      const encryptedMobileOtp = await encryptTextApi(mobileOtp);

      const response = await authHttp.post(
        apisPath?.verifyMobileUpdateOtp,
        {
          txnId: mobileOtpTxnId,
          otpValue: encryptedMobileOtp,
        },
        {
          headers: {
            accessToken: abdmAccessToken,
          },
        }
      );

      if (response?.data) {
        if (isExistingAccount) {
          // Scenario 2.2.1: Existing account - show alert and go to Step 4
          // Get ABHA number from the stored abhaData or fetch profile
          setAlertState({
            open: true,
            title: "Mobile number verified successfully",
            btnTxt: "OK",
            status: "success",
            onClose: async () => {
              // After mobile verification, show existing account alert
              // We need to get the ABHA profile to show the masked number
              await handleVerifyAbhaUser();
            }
          });
        } else {
          // Scenario 2.2.2: New account - continue to Step 3 for ABHA address creation
          setSnackState({
            open: true,
            message: "Mobile number verified successfully",
            severity: "success",
          });
          setStep(3);
        }
      } else {
        throw new Error(response?.data?.message || "Failed to verify mobile OTP");
      }
    } catch (error) {
      // Show error and stay on same step
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to verify mobile OTP",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Fetch ABHA Address Suggestions
  const fetchAbhaSuggestions = async () => {
    try {
      setIsLoading(true);

      const response = await authHttp.post(apisPath?.abhaEnrollSuggestions,
        {
          txnId: txnId,
        },
        {
          headers: {
            accessToken: abdmAccessToken,
          },

        }
      );

      if (response?.data) {
        const suggestions = response?.data?.abhaAddressList || [];
        setAbhaSuggestions(suggestions);
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to fetch ABHA suggestions",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Submit Preferred ABHA Address
  const handleSubmitAbhaAddress = async () => {
    try {
      setIsLoading(true);

      const response = await authHttp.post(
        apisPath?.preferredAbhaAddress,
        {
          txnId: txnId,
          abhaAddress: selectedAbhaAddress,
        },
        {
          headers: {
            accessToken: abdmAccessToken,
          },
        }
      );

      if (response?.data) {
        // Now verify with the auth token
        await handleVerifyAbhaUser();
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to set ABHA address",
        severity: "error",
      });
      setIsLoading(false);
    }
  };

  // Step 4: Verify ABHA User and Get Profile
  const handleVerifyAbhaUser = async (tokenOverride = null) => {
    // Use tokenOverride if provided, otherwise use authToken from state
    const tokenToUse = tokenOverride || authToken;
    try {
      const response = await authHttp.post(
        apisPath?.verifyAbhaOtpUser,
        {
          token: tokenToUse,
        },
        {
          headers: {
            accessToken: abdmAccessToken,
          },
        }
      );

      if (response?.data?.success) {
        const profileData = response?.data?.data;
        setAbhaData(profileData);

        // Check if this is an existing account
        if (isExistingAccount) {
          // Show "ABHA Account Exist" alert for existing accounts
          const maskedAbhaNumber = formatAbhaId(profileData?.ABHANumber);

          setAlertState({
            open: true,
            title: `ABHA Account Exist - We have found an ABHA ${maskedAbhaNumber} against the Aadhaar Number provided by you.`,
            btnTxt: "OK",
            status: "success",
            onClose: () => {
              // Move to Step 4 after alert closes
              setStep(4);
            }
          });
        } else {
          // New account - directly move to Step 4
          setStep(4);
        }
      } else {
        setAlertState({
          open: true,
          title: response?.data?.message,
          status: "success",
          btnTxt: "CLOSE",
          onClose: () => {
            handleClose();
            handleSearchModalClose();
          },
        });
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || error?.message || "Failed to verify ABHA user",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterBeneficiary = () => {
    if (abhaData) {
      setAbhaProfile(abhaData);
    }

    handleClose();
    // navigate(`${paths?.user?.commonPoint}${paths?.user?.patientRegistration}`);
    navigate(
      `${paths?.user?.commonPoint}${paths?.user?.patientRegistration}`,
      {
        state: {
          isExistingAccount,
        },
      }
    );
  };

  // Handle Close
  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Reset Modal
  const resetModal = () => {
    setStep(1);
    setAadhaar("");
    setConsentChecked(false);
    setOtp("");
    setMobile("");
    setMobileOtp("");
    setShowMobileOtpStep(false);
    setMobileOtpTxnId(null);
    setTxnId(null);
    setOtpMessage("");
    setAuthToken(null);
    setAbhaSuggestions([]);
    setSelectedAbhaAddress("");
    setAbhaData(null);
    setAbdmAccessToken(null);
    setIsExistingAccount(false);
    setEncryptedAadhaar(null);
    setEncryptedMobileNumber(null);
    resetAadhaarTimer();
    resetMobileTimer();
  };

  // Render step based on current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1AadhaarInput
            aadhaar={aadhaar}
            setAadhaar={setAadhaar}
            consentChecked={consentChecked}
            setConsentChecked={setConsentChecked}
            userName={userName}
            onNext={handleSendAadhaarOtp}
          />
        );
      case 2:
        return (
          <Step2OtpMobileVerification
            otp={otp}
            setOtp={setOtp}
            mobile={mobile}
            setMobile={setMobile}
            mobileOtp={mobileOtp}
            setMobileOtp={setMobileOtp}
            showMobileOtpStep={showMobileOtpStep}
            onVerifyAadhaarOtp={handleVerifyAadhaarOtp}
            onVerifyMobileOtp={handleVerifyMobileOtp}
            onResendAadhaarOtp={handleResendAadhaar}
            onResendMobileOtp={handleResendMobile}
            // Aadhaar OTP Timer Props
            aadhaarCanResend={canResendAadhaar}
            aadhaarFormattedTime={aadhaarFormattedTime}
            aadhaarResendCount={aadhaarResendCount}
            aadhaarMaxResendCount={aadhaarMaxResendCount}
            // Mobile OTP Timer Props
            mobileCanResend={canResendMobile}
            mobileFormattedTime={mobileFormattedTime}
            mobileResendCount={mobileResendCount}
            mobileMaxResendCount={mobileMaxResendCount}
          />
        );
      case 3:
        return (
          <Step3AbhaAddressSuggestions
            abhaSuggestions={abhaSuggestions}
            selectedAbhaAddress={selectedAbhaAddress}
            setSelectedAbhaAddress={setSelectedAbhaAddress}
            onFetchSuggestions={fetchAbhaSuggestions}
            onSubmit={handleSubmitAbhaAddress}
            onBack={() => setStep(2)}
          />
        );
      case 4:
        return (
          <Step4AbhaRegistered
            abhaData={abhaData}
            onRegisterBeneficiary={handleRegisterBeneficiary}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Commonmodel
      open={open}
      onClose={handleClose}
      title={step === 4 ? "Register New Beneficiary" : "ABHA Creation"}
      subtitle={"Create your Ayushman Bharat Health Account (ABHA)"}
      showActions={false}
      className="Abha_create_modal"
    >
      {/* Progress Indicator */}
      <div className="progress-container">
        <div className="progress-line"></div>
        <div className={`step ${step >= 1 ? "active" : ""}`}>
          <div className="step-icon">
            <VerifiedUserIcon />
          </div>
          <span>Aadhaar</span>
        </div>
        <div className={`step ${step >= 2 ? "active" : ""}`}>
          <div className="step-icon">
            <SmsIcon />
          </div>
          <span>Verify</span>
        </div>
        <div className={`step ${step >= 3 ? "active" : ""}`}>
          <div className="step-icon">
            <FingerprintIcon />
          </div>
          <span>ABHA Address Suggestions</span>
        </div>
        <div className={`step ${step >= 4 ? "active" : ""}`}>
          <div className="step-icon">
            <CheckCircleIcon />
          </div>
          <span>Complete</span>
        </div>
      </div>

      {/* Render Current Step */}
      {renderStep()}
    </Commonmodel>
  );
};

export default AbhaCreateModal;
